/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type React from 'react'
import colors from '@sportninja/common/constants/appColors'
import {
  ALL_ROLES,
  getRoleColors,
} from '@sportninja/common/constants/roleGroups'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { paragraphXSmall, weightMedium } from 'src/components/css'

interface RoleBadgeProps {
  /** Role id as the API sends it in `meta.roles[id][].role_id`. */
  roleId: string | number
  /** Role label, e.g. "Team Official". */
  name: string
  /** Extra emotion styles from the call site (spacing, alignment). */
  className?: string
}

/**
 * A pill naming one role the viewer holds on an entity.
 *
 * Colours come from `getRoleColors` so a role reads the same everywhere it
 * appears — the feed, the roster, the page header. An unknown id falls back
 * to a neutral pill rather than rendering nothing: the label is still useful.
 *
 * Outlined rather than filled on purpose. This is passive metadata sitting in
 * a header beside real controls, and a solid fill reads as something you can
 * press. The role colour carries on the text and a hairline border, which
 * keeps the per-role coding without competing with the page's actions.
 */
const RoleBadge: React.FC<RoleBadgeProps> = ({ roleId, name, className }) => {
  // `getRoleColors` falls back to a near-black foreground meant for light
  // surfaces, which would be unreadable here. Every id the API sends today is
  // mapped, but a role added server-side would otherwise render an invisible
  // badge — fall back to the standard light body colour instead.
  const roleColors = ALL_ROLES[String(roleId)]
    ? getRoleColors(roleId)
    : { bg: 'transparent', fg: colors.NEUTRAL_50 }

  return (
    <span
      className={className}
      css={[
        badgeStyle,
        css`
          color: ${roleColors.fg};
          border-color: ${roleColors.fg}59;
        `,
      ]}
    >
      {capitalizeWords(name)}
    </span>
  )
}

export default RoleBadge

// ---- styled ----
const badgeStyle = css`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  ${paragraphXSmall}
  ${weightMedium}
  background: transparent;
  /* Colour set per role at the call site; this only fixes the width. */
  border: 1px solid transparent;
  white-space: nowrap;
  flex-shrink: 0;
  vertical-align: middle;
`
